import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "motion/react";
import { PANNES } from "@/config/pannes-persianas";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WhatsAppFloating from "@/components/feature/WhatsAppFloating";
import CookieBanner from "@/components/feature/CookieBanner";
import SEO from "@/components/feature/SEO";

function CrossLinkOtrasAverias({ currentSlug }: { currentSlug: string }) {
  const others = Object.values(PANNES).filter((p) => p.slug !== currentSlug);
  return (
    <section className="bg-surface-alt py-16 md:py-24">
      <div className="max-w-container mx-auto px-4 md:px-6">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary text-center mb-4">
          ¿Tu problema es diferente?
        </h2>
        <p className="font-body text-base text-text-secondary text-center mb-10 max-w-xl mx-auto">
          Explora otras averías comunes de persianas y cómo las resolvemos.
        </p>
        <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
          {others.map((p) => (
            <Link
              key={p.slug}
              to={`/persianas/pannes/${p.slug}`}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-border rounded-pill font-body font-semibold text-sm text-text-primary hover:bg-primary hover:text-white hover:border-primary transition-all"
            >
              {p.h1.split(" — ")[0]}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQAccordion({ items }: { items: Array<{ q: string; a: string }> }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {items.map((f, i) => (
        <div key={i} className="border border-border rounded-xl bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <span className="font-body font-semibold text-base text-text-primary pr-4">{f.q}</span>
            <span className={`text-primary text-xl transition-transform ${open === i ? "rotate-180" : ""}`}>
              <i className="ri-arrow-down-s-line"></i>
            </span>
          </button>
          {open === i && (
            <div className="px-5 pb-4">
              <p className="font-body text-sm text-text-secondary leading-relaxed">{f.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function PannePersianaPage() {
  const { slug } = useParams<{ slug: string }>();
  const data = slug ? PANNES[slug] : undefined;

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 py-24 text-center">
            <h1 className="font-heading font-extrabold text-3xl text-text-primary mb-4">Avería no encontrada</h1>
            <p className="font-body text-lg text-text-secondary mb-8">La página que buscas no existe o ha sido movida.</p>
            <Link to="/persianas" className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold px-8 py-4 rounded-pill transition-colors">
              Volver a Persianas
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: data.h1,
    step: [
      {
        "@type": "HowToStep",
        name: "Identificar síntomas",
        text: "Revisar los síntomas habituales listados en la página para reconocer la avería.",
      },
      {
        "@type": "HowToStep",
        name: "Diagnosticar causa",
        text: `Determinar cuál de las causas más frecuentes corresponde: ${data.causas.map((c) => c.cause).join(", ")}.`,
      },
      {
        "@type": "HowToStep",
        name: "Llamar al técnico",
        text: "Contactar al 951 12 07 70 para reparación 24h en Málaga y Costa del Sol.",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        ogType="article"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Persianas", path: "/persianas" },
          { name: data.h1.split(" — ")[0] },
        ]}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />

      <Header />
      <Breadcrumbs />

      <main>
        {/* HERO */}
        <section className="bg-white pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 md:px-6 py-12 md:py-20">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-accent text-text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill mb-6">
                <span>🔧</span> Reparación 24h · Desde 60€ · Málaga y Costa del Sol
              </div>
              <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-text-primary mb-5 leading-tight">
                {data.h1}
              </h1>
              <p className="font-body text-lg md:text-xl text-text-secondary leading-relaxed mb-8 max-w-2xl mx-auto">
                {data.intro}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
                <a
                  href="tel:+34951120770"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-8 py-4 rounded-pill transition-colors shadow-lg shadow-primary/20 whitespace-nowrap"
                >
                  <span>📞</span> Llamar ahora: 951 12 07 70
                </a>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20tengo%20un%20problema%20con%20mi%20persiana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
                >
                  <span>💬</span> WhatsApp 627 21 33 47
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SÍNTOMAS */}
        <section className="bg-surface-alt py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-6">
                Síntomas habituales
              </h2>
              <ul className="space-y-3">
                {data.sintomas.map((s, i) => (
                  <motion.li
                    key={i}
                    className="flex items-start gap-3 bg-white rounded-xl px-5 py-4 border border-border"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <span className="text-primary mt-0.5"><i className="ri-checkbox-circle-line text-lg"></i></span>
                    <span className="font-body text-base text-text-primary">{s}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* CAUSAS */}
        <section className="bg-white py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-2">
                Causas más frecuentes
              </h2>
              <p className="font-body text-base text-text-secondary mb-8">
                Basado en más de 3.000 reparaciones de persianas en Málaga. Estos porcentajes reflejan la frecuencia real de cada causa.
              </p>
              <div className="space-y-5">
                {data.causas.map((c, i) => (
                  <motion.div
                    key={i}
                    className="bg-surface-alt rounded-xl p-5 md:p-6 border border-border"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-body font-bold text-base text-text-primary">{c.cause}</h3>
                      <span className="font-heading font-bold text-lg text-primary">{c.pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-border rounded-full overflow-hidden mb-3">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${c.pct}%` }}
                      />
                    </div>
                    <p className="font-body text-sm text-text-secondary leading-relaxed">{c.detalle}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* DIAGNÓSTICO */}
        <section className="bg-primary/5 py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-14 h-14 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-stethoscope-line text-2xl"></i>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
                Diagnóstico rápido sin coste
              </h2>
              <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed mb-6 max-w-2xl mx-auto">
                En menos de 5 minutos identificamos la causa exacta de tu avería. No cobramos por diagnosticar: solo si decides reparar con nosotros. Nuestros técnicos llevan stock de piezas en el vehículo y resuelven el 90% de las averías en la primera visita.
              </p>
              <a
                href="tel:+34951120770"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-8 py-4 rounded-pill transition-colors shadow-lg shadow-primary/20"
              >
                <span>📞</span> Pedir diagnóstico gratis: 951 12 07 70
              </a>
            </div>
          </div>
        </section>

        {/* COSTES */}
        <section className="bg-white py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary text-center mb-2">
                Costes orientativos
              </h2>
              <p className="font-body text-base text-text-secondary text-center mb-8 max-w-xl mx-auto">
                Precios aproximados para Málaga capital. Cada persiana es diferente: presupuesto exacto sin compromiso tras visita técnica.
              </p>
              <div className="bg-surface-alt rounded-3xl shadow-sm overflow-hidden border border-border">
                {data.costes.map((c, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== data.costes.length - 1 ? "border-b border-border" : ""}`}
                  >
                    <span className="font-body text-base md:text-lg text-text-primary">{c.servicio}</span>
                    <span className="font-heading font-bold text-lg md:text-xl text-primary">{c.precio}</span>
                  </div>
                ))}
              </div>
              <p className="text-center font-body text-xs text-text-secondary mt-4">
                Precios orientativos. Presupuesto exacto sin compromiso tras visita técnica.
              </p>
            </div>
          </div>
        </section>

        {/* NO HACERLO */}
        <section className="bg-red-50 py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-2">
                Cuándo NO intentar repararlo tú mismo
              </h2>
              <p className="font-body text-base text-text-secondary mb-8">
                Algunas situaciones requieren herramientas específicas y conocimiento técnico. Intentar arreglarlas sin experiencia puede empeorar la avería y aumentar el coste final.
              </p>
              <ul className="space-y-3">
                {data.noHacerlo.map((n, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white rounded-xl px-5 py-4 border border-red-100">
                    <span className="text-red-500 mt-0.5"><i className="ri-alert-line text-lg"></i></span>
                    <span className="font-body text-sm md:text-base text-text-primary leading-relaxed">{n}</span>
                  </li>
                ))}
              </ul>
              {data.urgencia && (
                <div className="mt-6 bg-red-100 border border-red-200 rounded-xl p-5 text-center">
                  <p className="font-body font-semibold text-base text-red-700">{data.urgencia}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA BLOCK */}
        <section className="bg-text-primary py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-4">
              Reparación hoy mismo en Málaga
            </h2>
            <p className="font-body text-base md:text-lg text-slate-300 mb-8 max-w-xl mx-auto">
              Llámanos ahora y un técnico estará en tu domicilio en menos de 4 horas (Málaga capital) o 24h (Costa del Sol).
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <a
                href="tel:+34951120770"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-8 py-4 rounded-pill transition-colors shadow-lg shadow-primary/20 whitespace-nowrap"
              >
                <span>📞</span> 951 12 07 70
              </a>
              <a
                href="https://wa.me/34627213347?text=Hola%2C%20tengo%20un%20problema%20con%20mi%20persiana"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
              >
                <span>💬</span> WhatsApp 627 21 33 47
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-6">
              <span className="flex items-center gap-1.5 font-body text-sm text-slate-400">
                <span className="text-green-400">✓</span> +10 años de experiencia
              </span>
              <span className="flex items-center gap-1.5 font-body text-sm text-slate-400">
                <span className="text-green-400">✓</span> Garantía 2 años
              </span>
              <span className="flex items-center gap-1.5 font-body text-sm text-slate-400">
                <span className="text-green-400">✓</span> Presupuesto sin compromiso
              </span>
            </div>
          </div>
        </section>

        {/* FAQS */}
        <section className="bg-white py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-2">
                Preguntas frecuentes
              </h2>
              <p className="font-body text-base text-text-secondary mb-8">
                Respuestas basadas en miles de reparaciones reales en Málaga y la Costa del Sol.
              </p>
              <FAQAccordion items={data.faqs} />
            </div>
          </div>
        </section>

        <CrossLinkOtrasAverias currentSlug={data.slug} />
      </main>

      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}